import {Injectable} from "@angular/core";
import {
  catchError,
  delay,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  of,
  retry,
  switchMap, take,
  tap,
  throwError,
  timer,
  zip
} from "rxjs";
import {NavigationEnd, Router} from "@angular/router";

import {NetworkService, ConnectionStatus} from "./network.service";
import {JsBridgeService} from "./js-bridge.service";
import {LocalStorageService} from "./local-storage.service";
import {UtentiService} from "../../api-clients/generated/services";
import {GlobalManagerComuneService} from "./pratiche/global-manager-comune.service";
import {GlobalManagerFigliService} from "./global-manager-figli.service";
import {NavController} from "@ionic/angular/standalone";
import {PathEnum} from "../shared/enums/path-enum";
import {TextSizeService} from "./text-size.service";
import {TokenExpiryService} from "./token-expiry.service";
import {NotificationService} from "./notification.service";
import {environment} from "../../environments/environment";


@Injectable({
  providedIn: 'root'
})
export class AppInitService {

  networkStatus: ConnectionStatus | undefined;

  constructor(
    private router: Router,
    private networkService: NetworkService,
    private jsBridgeService: JsBridgeService,
    private localStorageService: LocalStorageService,
    private userControllerService: UtentiService,
    private globalManagerComuneService: GlobalManagerComuneService,
    private globalManagerFigliService: GlobalManagerFigliService,
    private navCtrl: NavController,
    private textSizeService: TextSizeService,
    private tokenExpiryService: TokenExpiryService,
    private notificationService: NotificationService) {
  }

  init() {
    console.log('[AppInit] init() START');

    return new Observable((subscriber) => {

      // Wait for native bridge ready, then check sessionStorage for session recovery.
      this.jsBridgeService.getSharedValues([]);

      from(this.jsBridgeService.valuesReady).pipe(

        tap((v) => {
          console.log('[AppInit] valuesReady resolved:', v);
        }),

        switchMap(() => from(this.textSizeService.init())),

        switchMap(() =>
          zip([
            this.init$(),
            timer(3000)
          ]).pipe(
            map(value => value[0]),
          )
        ),

        tap(() => {
          console.log('[AppInit] NavigationEnd listener setup');
          const sub = this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            take(1),
          ).subscribe(() => {
            console.log('[AppInit] NavigationEnd fired → appInitEnd()');
            sub.unsubscribe();
            this.jsBridgeService.appInitEnd();
          });
        }),

      ).subscribe({
        next: value => {
          console.log('[AppInit] init() COMPLETED', value);
          subscriber.complete();
        },
        error: err => {
          console.error('[AppInit] init() ERROR', err);
          subscriber.error(err);
        }
      });
    });
  }


  async getNetWorkStatus() {
    this.networkStatus = {connected: navigator.onLine, connectionType: navigator.onLine ? 'wifi' : 'none'};
    console.log('[Network] status:', this.networkStatus);
    return !this.networkStatus?.connected;
  }


  init$() {
    return from(this.getNetWorkStatus()).pipe(
      tap((isOffline) =>
        this.networkService.sendStatusChangeMsg(
          {
            connected: !isOffline,
            connectionType: 'unknown'
          })
      ),
      switchMap((isOffline) => this.initAfterCheckNetwork$(isOffline)),
    )
  }

  initAfterCheckNetwork$(isOffline: boolean) {
    // Skip init per callback SPID (verrà gestito dal CallbackComponent)
    if (this.isSpidCallbackPath()) {
      return of(null);
    }

    // Se ci sono token in sessionStorage (ripristinati nel costruttore di LocalStorageService),
    // tenta il recovery della sessione: ricarica profilo, anagrafiche e comune.
    if (this.localStorageService.getAccessToken()) {
      console.log('[AppInit] Token found in sessionStorage, attempting session recovery');
      this.tokenExpiryService.startMonitoring(this.localStorageService.getAccessToken()!);
      return this.recoverSession$();
    }

    // Nessun token → login page
    return of(null);
  }

  private recoverSession$(): Observable<any> {
    return this.fetchInitialData$().pipe(
      tap(({profile, comuneRestored}) => {
        if (profile?.id) {
          this.jsBridgeService.login({accessToken: this.localStorageService.getAccessToken()!, userId: profile.id});
        }

        const browserPath = location.pathname + location.search;
        const hasExplicitRoute = (environment as any).respectBrowserUrl
          && browserPath !== '/'
          && browserPath !== '';

        if (hasExplicitRoute) {
          console.log('[AppInit] Respecting browser URL:', browserPath);
          this.navCtrl.navigateRoot(browserPath);
        } else if (comuneRestored) {
          this.navCtrl.navigateRoot(PathEnum.PAGES + PathEnum.HOME);
        } else {
          this.navCtrl.navigateRoot(PathEnum.PAGES + PathEnum.SELEZIONE_COMUNE);
        }
        console.log('[AppInit] Session recovered, navigating to', comuneRestored ? 'HOME' : 'SELEZIONE_COMUNE');
      }),
      catchError((err) => {
        if (err?.status === 401) {
          return of(null);
        }
        console.error('[AppInit] Session recovery failed after retry', err);
        return this.localStorageService.cleanPreferenceToken$().pipe(
          tap(() => {
            this.navCtrl.navigateRoot(PathEnum.LOGIN, {replaceUrl: true});
            this.notificationService.openToast({
              message: 'Impossibile caricare i dati. Effettua nuovamente il login.',
              color: 'warning'
            });
          }),
          map(() => null),
        );
      }),
    );
  }

  private fetchInitialData$(): Observable<{profile: any, comuneRestored: boolean}> {
    return forkJoin([
      this.userControllerService.getUser(),
      this.userControllerService.getAnagraficheUtente()
    ]).pipe(
      switchMap(([profile, anagrafiche]) => forkJoin([
        this.localStorageService.setPreferenceProfile$(profile),
        this.localStorageService.setAnagrafiche$(anagrafiche),
      ]).pipe(map(() => profile))),
      switchMap((profile) =>
        this.globalManagerFigliService.getFigli$().pipe(map(() => profile))
      ),
      switchMap((profile) =>
        this.globalManagerComuneService.loadComuni$().pipe(
          switchMap(() => this.globalManagerComuneService.tryRestoreFromStorage$()),
          map(comuneRestored => ({profile, comuneRestored})),
        )
      ),
      retry({
        count: 1,
        delay: (error) => {
          if (error?.status === 401) {
            return throwError(() => error);
          }
          console.warn('[AppInit] Initial data fetch failed, retrying in 2s...', error);
          return timer(2000);
        },
      }),
    );
  }

  private isSpidCallbackPath(): boolean {
    return location.pathname.includes('/auth/callback');
  }


}
